import React, { useEffect, useState } from 'react';
import { MessageCircle, Search, Plus, Bell, Send, User } from 'lucide-react';
import AuthContext from '../context/AuthContext';
import Modal from '../components/Modal';
import { apiClient } from '../apiClient';
import ImageUploadButton from '../components/ImageUploadButton';

const TABS = [
  { value: 'messages', label: 'Messages', icon: MessageCircle },
  { value: 'notifications', label: 'Notifications', icon: Bell }
];

function ThreadPreview({ thread, isActive, onSelect }) {
  const participant = thread.participant || {};
  const name = participant.name || participant.username || 'Unknown';
  const lastMessage = thread.last_message?.body
    || (thread.last_message?.image_url ? 'Image attachment' : '')
    || (thread.last_message?.gif_url ? 'GIF attachment' : '')
    || 'No messages yet';
  return (
    <button
      type="button"
      className={`inbox-thread ${isActive ? 'active' : ''}`}
      onClick={onSelect}
    >
      <div className="inbox-thread-avatar">
        {participant.avatar_url ? (
          <img src={participant.avatar_url} alt={name} />
        ) : (
          <User className="w-4 h-4" />
        )}
      </div>
      <div className="inbox-thread-body">
        <div className="inbox-thread-name">{name}</div>
        <div className="inbox-thread-preview">{lastMessage}</div>
      </div>
      <div className="inbox-thread-meta">
        {thread.updated_at ? new Date(thread.updated_at).toLocaleDateString() : ''}
      </div>
    </button>
  );
}

const linkifyText = (text) => {
  const value = text || '';
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return value.split(urlRegex).map((part, index) => {
    const isUrl = /^https?:\/\/\S+$/.test(part);
    if (isUrl) {
      return (
        <a key={index} href={part} target="_blank" rel="noreferrer" className="inbox-link">
          {part}
        </a>
      );
    }
    return <span key={index}>{part}</span>;
  });
};

function GiphyPickerModal({ open, onClose, onSelect }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      setLoading(false);
      setError('');
      return;
    }
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = query.trim().length >= 2
          ? await apiClient.get('/api/inbox/gifs/search', { query: query.trim() })
          : await apiClient.get('/api/inbox/gifs/trending');
        if (active) setResults(data || []);
      } catch (err) {
        if (active) setResults([]);
        if (active) setError(err?.message || 'Unable to load GIFs.');
      } finally {
        if (active) setLoading(false);
      }
    };
    const timeoutId = setTimeout(load, 300);
    return () => {
      active = false;
      clearTimeout(timeoutId);
    };
  }, [open, query]);

  return (
    <Modal
      open={open}
      title="Choose a GIF"
      onClose={onClose}
      footer={(
        <div className="modal-actions">
          <button className="btn-secondary" type="button" onClick={onClose}>Close</button>
        </div>
      )}
    >
      <div className="modal-field">
        <label>Search GIPHY</label>
        <input
          className="input-glass"
          placeholder="Search GIFs"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      {loading ? (
        <div className="spinner" />
      ) : error ? (
        <div className="error-banner">{error}</div>
      ) : results.length === 0 ? (
        <div className="empty-state">
          <p className="text-gray-400">No GIFs found. Try another search.</p>
        </div>
      ) : (
        <div className="gif-grid">
          {results.map((gif) => (
            <button
              key={gif.id}
              type="button"
              className="gif-card"
              onClick={() => {
                onSelect(gif.url);
                onClose();
              }}
            >
              <img src={gif.preview_url || gif.url} alt="gif" />
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}

export default function InboxPage() {
  const { user } = React.useContext(AuthContext);
  const [tab, setTab] = useState('messages');
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeRecipient, setComposeRecipient] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeAttachment, setComposeAttachment] = useState(null);
  const [composeGifModalOpen, setComposeGifModalOpen] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [gifModalOpen, setGifModalOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [viewMode, setViewMode] = useState('list');

  const loadThreads = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiClient.get('/api/inbox/threads');
      setThreads(data || []);
      if (data && data.length && !activeThread) {
        setActiveThread(data[0]);
      }
    } catch (err) {
      setError(err.message || 'Unable to load inbox.');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (threadId) => {
    if (!threadId) return;
    try {
      const data = await apiClient.get(`/api/inbox/threads/${threadId}`);
      setMessages(data?.messages || []);
    } catch (err) {
      setMessages([]);
    }
  };

  const loadNotifications = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiClient.get('/api/hub/notifications');
      setNotifications(data || []);
    } catch (err) {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    if (tab === 'messages') {
      loadThreads();
    } else {
      loadNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user]);

  useEffect(() => {
    if (activeThread?.thread_id) {
      loadMessages(activeThread.thread_id);
    }
  }, [activeThread?.thread_id]);

  useEffect(() => {
    setReplyBody('');
  }, [activeThread?.thread_id]);

  useEffect(() => {
    setViewMode('list');
  }, [tab]);

  const handleSend = async () => {
    if (!composeRecipient.trim()) return;
    if (!composeBody.trim() && !composeAttachment) return;
    setSending(true);
    try {
      const created = await apiClient.post('/api/inbox/threads', {
        identifier: composeRecipient.trim(),
        message: composeBody.trim(),
        image_url: composeAttachment?.type === 'image' ? composeAttachment.url : null,
        gif_url: composeAttachment?.type === 'gif' ? composeAttachment.url : null
      });
      setComposeOpen(false);
      setComposeRecipient('');
      setComposeBody('');
      setComposeAttachment(null);
      if (created) {
        setThreads((prev) => {
          const existing = prev.filter((t) => t.thread_id !== created.thread_id);
          return [created, ...existing];
        });
        setActiveThread(created);
      }
    } catch (err) {
      setError(err.message || 'Unable to send message.');
    } finally {
      setSending(false);
    }
  };

  const handleReply = async () => {
    if (!activeThread?.thread_id) return;
    if (!replyBody.trim() && !attachment) return;
    setSending(true);
    try {
      const sent = await apiClient.post(`/api/inbox/threads/${activeThread.thread_id}/messages`, {
        body: replyBody.trim(),
        image_url: attachment?.type === 'image' ? attachment.url : null,
        gif_url: attachment?.type === 'gif' ? attachment.url : null
      });
      setMessages((prev) => [...prev, sent]);
      setThreads((prev) => {
        const updated = prev.map((thread) => (
          thread.thread_id === activeThread.thread_id
            ? { ...thread, last_message: sent, updated_at: sent.created_at }
            : thread
        ));
        return updated.sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
      });
      setReplyBody('');
      setAttachment(null);
    } catch (err) {
      setError(err.message || 'Unable to send message.');
    } finally {
      setSending(false);
    }
  };

  const filteredThreads = threads.filter((thread) => {
    const target = (thread.participant?.name || thread.participant?.username || '').toLowerCase();
    return target.includes(search.toLowerCase());
  });

  const handleImageAttach = (file, setter) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Only image files are supported.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const url = reader.result;
      const type = file.type === 'image/gif' ? 'gif' : 'image';
      setter({ type, url });
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="inbox-page">
      <div className="page-header">
        <div className="page-header-top">
          <div>
            <h1 className="text-3xl font-bold mb-2">Inbox</h1>
            <p className="text-gray-400">Direct messages and system notifications in one place.</p>
          </div>
          {tab === 'messages' && (
            <button className="btn-primary" type="button" onClick={() => setComposeOpen(true)}>
              <Plus className="w-4 h-4" /> New Message
            </button>
          )}
        </div>
        <div className="page-header-actions">
          <div className="search-field">
            <Search className="w-4 h-4" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search conversations"
            />
          </div>
          <div className="hub-tabs">
            {TABS.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.value}
                  type="button"
                  className={`hub-tab ${tab === item.value ? 'active' : ''}`}
                  onClick={() => setTab(item.value)}
                >
                  <Icon className="w-4 h-4" /> {item.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {tab === 'messages' ? (
        <div className="inbox-stage">
          {viewMode === 'list' && (
            <div className="card-glass inbox-panel">
              <div className="inbox-section-header">
                <div>
                  <div className="section-title">Conversations</div>
                  <div className="text-xs text-gray-400">{filteredThreads.length} total</div>
                </div>
                <button className="btn-secondary btn-compact" type="button" onClick={() => setComposeOpen(true)}>
                  <Plus className="w-4 h-4" /> New Message
                </button>
              </div>
              {loading ? (
                <div className="spinner" />
              ) : filteredThreads.length === 0 ? (
                <div className="empty-state">
                  <p className="text-gray-400">No conversations yet.</p>
                </div>
              ) : (
                <div className="inbox-thread-list">
                  {filteredThreads.map((thread) => (
                    <ThreadPreview
                      key={thread.thread_id}
                      thread={thread}
                      isActive={activeThread?.thread_id === thread.thread_id}
                      onSelect={() => {
                        setActiveThread(thread);
                        setViewMode('thread');
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {viewMode === 'thread' && activeThread && (
            <div className="card-glass inbox-panel inbox-conversation">
              <div className="inbox-conversation-header">
                <div className="inbox-conversation-left">
                  <button className="btn-secondary btn-compact" type="button" onClick={() => setViewMode('list')}>
                    Back
                  </button>
                  <div className="inbox-conversation-title">
                    {activeThread.participant?.name || activeThread.participant?.username || 'Conversation'}
                  </div>
                </div>
                <span className="badge badge-outline">Direct Message</span>
              </div>
              <div className="inbox-messages">
                {messages.length === 0 ? (
                  <div className="empty-state text-gray-400">Start the conversation.</div>
                ) : (
                  messages.map((message) => (
                    <div
                      key={message.message_id}
                      className={`inbox-message ${message.sender_id === user?.user_id ? 'self' : ''}`}
                    >
                      {message.body && (
                        <div className="inbox-message-bubble">{linkifyText(message.body)}</div>
                      )}
                      {message.image_url && (
                        <img className="inbox-message-media" src={message.image_url} alt="attachment" />
                      )}
                      {message.gif_url && (
                        <img className="inbox-message-media" src={message.gif_url} alt="gif" />
                      )}
                      <div className="inbox-message-meta">
                        {new Date(message.created_at).toLocaleString()}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="inbox-attachment-row">
                <ImageUploadButton
                  tooltip="Upload image"
                  onSelect={(file) => handleImageAttach(file, setAttachment)}
                />
                <button className="icon-chip" type="button" onClick={() => setGifModalOpen(true)} data-tooltip="Add GIF" aria-label="Add GIF">
                  <span className="gif-pill">GIF</span>
                </button>
                {attachment && (
                  <button className="btn-secondary btn-compact" type="button" onClick={() => setAttachment(null)}>
                    Remove Attachment
                  </button>
                )}
              </div>
              {attachment && (
                <div className="inbox-attachment-preview">
                  <img src={attachment.url} alt="attachment preview" />
                </div>
              )}
              <div className="inbox-composer">
                <input
                  className="input-glass"
                  placeholder="Type a message..."
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                />
                <button className="btn-primary btn-compact" type="button" onClick={handleReply} disabled={sending}>
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="inbox-stage">
          <div className="card-glass inbox-panel">
            {loading ? (
              <div className="spinner" />
            ) : notifications.length === 0 ? (
              <div className="empty-state">
                <p className="text-gray-400">No notifications yet.</p>
              </div>
            ) : (
              <div className="social-post-list">
                {notifications.map((note) => (
                  <div key={note.notification_id} className="card-glass social-post-card">
                    <div className="text-sm text-gray-400">{note.type}</div>
                    <div className="text-sm">{JSON.stringify(note.payload_json)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <Modal
        open={composeOpen}
        title="New Message"
        onClose={() => setComposeOpen(false)}
        footer={(
          <div className="modal-actions">
            <button className="btn-secondary" type="button" onClick={() => setComposeOpen(false)}>Cancel</button>
            <button className="btn-primary" type="button" onClick={handleSend} disabled={sending}>
              {sending ? 'Sending...' : 'Send Message'}
            </button>
          </div>
        )}
      >
        <div className="modal-field">
          <label>Recipient</label>
          <input
            className="input-glass"
            placeholder="Enter email or @username"
            value={composeRecipient}
            onChange={(e) => setComposeRecipient(e.target.value)}
          />
        </div>
        <div className="modal-field">
          <label>Message</label>
          <textarea
            className="input-glass"
            rows="4"
            value={composeBody}
            onChange={(e) => setComposeBody(e.target.value)}
            placeholder="Write a quick hello..."
          />
        </div>
        <div className="modal-field">
          <label>Attachments</label>
          <div className="inbox-attachment-row">
            <ImageUploadButton
              tooltip="Upload image"
              onSelect={(file) => handleImageAttach(file, setComposeAttachment)}
            />
            <button className="icon-chip" type="button" onClick={() => setComposeGifModalOpen(true)} data-tooltip="Add GIF" aria-label="Add GIF">
              <span className="gif-pill">GIF</span>
            </button>
            {composeAttachment && (
              <button className="btn-secondary btn-compact" type="button" onClick={() => setComposeAttachment(null)}>
                Remove Attachment
              </button>
            )}
          </div>
          {composeAttachment && (
            <div className="inbox-attachment-preview">
              <img src={composeAttachment.url} alt="attachment preview" />
            </div>
          )}
        </div>
      </Modal>

      <GiphyPickerModal
        open={gifModalOpen}
        onClose={() => setGifModalOpen(false)}
        onSelect={(url) => setAttachment({ type: 'gif', url })}
      />

      <GiphyPickerModal
        open={composeGifModalOpen}
        onClose={() => setComposeGifModalOpen(false)}
        onSelect={(url) => setComposeAttachment({ type: 'gif', url })}
      />
    </div>
  );
}
